import { mapGetters, mapState } from 'vuex'
import { OrderLine } from '@/entities/Order'
import { orderLineTypes, orderLineItemEntities, currencyTypes } from '@/config'
import { isPayable } from '@/lib/helpers/AccountUtils'
import { colonHourToMs } from '@/lib/helpers/HoursFormatter'
import moment from 'moment'
const { RESERVATION_METHOD, RESERVATION_METHOD_ATTENDEE_TYPE, PRODUCT, CATALOG_PRODUCT_MODIFIER_OPTION } = orderLineItemEntities
const { SALE } = orderLineTypes

export default {
  computed: {
    reservationMethodType () {
      return this.reservationMethod ? this.reservationMethod.type : null
    },
    guest: {
      get () {
        return this.activityReservations.edited.guest
      },
      async set (value) {
        if (value) {
          const guestAccountId = this.guestAccount(value)
          if (guestAccountId) {
            await this.$store.dispatch('accounts/getAccount', { id: guestAccountId }).then((account) => {
              this.account = account
            })
          } else {
            this.account = null
          }
        }
        this.$store.commit('activityReservations/setField', { field: 'guest', value })
      },
    },
    account: {
      get () {
        return this.activityReservations.edited.account
      },
      set (value) {
        this.$store.commit('activityReservations/setField', {
          field: 'account',
          value,
        })
      },
    },
    date: {
      get () {
        return this.activityReservations.edited.date
          ? moment(this.activityReservations.edited.date).format('YYYY-MM-DD')
          : null
      },
      set (value) {
        this.$store.commit('activityReservations/setField', {
          field: 'date',
          value: moment(value, 'YYYY-MM-DD'),
        })
      },
    },
    slot: {
      get () {
        return this.activityReservations.edited.slot
      },
      set (value) {
        this.$store.commit('activityReservations/setField', {
          field: 'slot',
          value,
        })
      },
    },
    requestedMoment () {
      return this.date && this.slot !== null ? moment(this.date, 'YYYY-MM-DD').add(colonHourToMs(this.slot)) : null
    },
    room: {
      get () {
        return this.activityReservations.edited.room
      },
      set (value) {
        this.$store.commit('activityReservations/setField', {
          field: 'room',
          value,
        })
      },
    },
    nominalAttendees: {
      get () {
        return this.activityReservations.edited.attendees ? this.activityReservations.edited.attendees.nominal || [] : []
      },
      set (value) {
        this.$store.commit('activityReservations/setField', {
          field: 'nominal',
          value,
        })
      },
    },
    numeralAttendees: {
      get () {
        return this.activityReservations.edited.attendees ? this.activityReservations.edited.attendees.numeral || [] : []
      },
      set ({ attendeeTypeId, quantity }) {
        const array = this.numeralAttendees
        let i = array.findIndex((attendeeType) => {
          return attendeeType.id === attendeeTypeId
        })
        i = i === -1
          ? array.length
          : i
        if (quantity) {
          array[i] = {
            id: attendeeTypeId,
            quantity: quantity,
          }
        } else {
          array.splice(i, 1)
        }
        this.$store.commit('activityReservations/setField', { field: 'numeral', value: array })
      },
    },
    people () {
      return this.nominalAttendees.length + this.numeralAttendees.reduce((acc, { quantity }) => acc + quantity, 0)
    },
    comments: {
      get () {
        return this.activityReservations.edited.comments
      },
      set (value) {
        this.$store.commit('activityReservations/setField', {
          field: 'comments',
          value,
        })
      },
    },
    orderLines () {
      const orderLines = []
      orderLines.push(...this.reservationOrderLines)
      if (this.reservationMethodType === 'ROOM_SERVICE') {
        orderLines.push(...this.cartProductOrderLines)
      }
      return orderLines
    },
    reservationOrderLines () {
      const reservationOrderLines = []
      if (this.reservationPriceOrderLine) {
        reservationOrderLines.push(this.reservationPriceOrderLine)
      }
      reservationOrderLines.push(...this.attendeeTypeOrderLines)
      return reservationOrderLines
    },
    reservationPriceOrderLine () {
      const reservationPrice = this.reservationMethod ? this.reservationMethod.reservationPrice || [] : []
      const pricingGroup = this.findPricingGroup({ pricingGroups: reservationPrice })
      if (!pricingGroup) return null
      const orderLine = new OrderLine({
        itemEntity: RESERVATION_METHOD,
        orderLineType: SALE,
        quantity: 1,
        itemId: this.reservationMethod.id,
        name: this.$t('activity_reservation.name'),
        price: pricingGroup.prices[0],
      })
      return orderLine
    },
    attendeeTypeOrderLines () {
      const toOrderLines = []
      this.nominalAttendees.forEach((guestId) => {
        const guest = this.guestsInBookings.find(extendedGuest => {
          return extendedGuest.guest.id === guestId
        })
        if (guest) {
          const guestTypeId = guest.guest.typeId
          let attendeeType = this.attendeeTypes.find(aT => {
            return aT.guestTypeIds.includes(guestTypeId)
          })
          if (!attendeeType) {
            attendeeType = this.attendeeTypes.find(aT => {
              return !aT.guestTypeIds.length
            })
          }
          if (attendeeType) {
            const pricingGroup = this.findPricingGroup({ pricingGroups: attendeeType.attendeePrices })
            if (pricingGroup) {
              addToToOrderLines(pricingGroup, 1, attendeeType)
            }
          }
        }
      })
      this.numeralAttendees.forEach((numeralAttendee) => {
        const attendeeType = this.attendeeTypes.find(aT => aT.id === numeralAttendee.id)
        if (attendeeType) {
          const pricingGroup = this.findPricingGroup({ pricingGroups: attendeeType.attendeePrices })
          if (pricingGroup) {
            addToToOrderLines(pricingGroup, numeralAttendee.quantity, attendeeType)
          }
        }
      })
      function addToToOrderLines (pricingGroup, quantity, attendeeType) {
        const index = toOrderLines.findIndex(data => {
          return data.attendeeType.id === attendeeType.id
        })
        if (index !== -1) {
          toOrderLines[index].quantity += quantity
        } else {
          toOrderLines.push({ price: pricingGroup.prices[0], quantity, attendeeType })
        }
      }
      return toOrderLines.map((data) => {
        const { price, quantity, attendeeType } = data
        const orderLine = new OrderLine({
          itemEntity: RESERVATION_METHOD_ATTENDEE_TYPE,
          itemId: attendeeType.id,
          name: attendeeType.name,
          orderLineType: SALE,
          quantity,
          price,
        })
        return orderLine
      })
    },
    totalByCurrencies () {
      return this.reservationMethodType === 'ROOM_SERVICE'
        ? this.getTotal({
          reservationOrderLines: this.reservationOrderLines,
        })
        : this.getTotal({
          noProducts: true,
          reservationOrderLines: this.reservationOrderLines,
        })
    },
    orderHasPrice () {
      return this.totalByCurrencies.some(totalByCurrency => totalByCurrency.amount > 0)
    },
    orderIsUnpayable () {
      return this.orderHasPrice && !isPayable({ totalByCurrencies: this.totalByCurrencies, account: this.account })
    },
    isActiveAccountRequired () {
      return this.orderHasPrice && !this.account.active
    },
    isProductNeeded () {
      return this.reservationMethod.preOrder === 'MANDATORY' && !this.cartProductOrderLines.length
    },
    highestTotalByCurrency () {
      return this.getHighestTotalByCurrency({ orderLines: this.orderLines })
    },
    getHighestTotalByCurrency: () => ({ orderLines }) => {
      var totals = []
      orderLines.forEach((orderLine) => {
        if (orderLine.orderLineType === SALE && [PRODUCT, CATALOG_PRODUCT_MODIFIER_OPTION].includes(orderLine.itemEntity)) {
          orderLine.price.forEach((price) => {
            const index = totals.findIndex(item => {
              return item.currencyId === price.currencyId
            })
            const amount = price.amount * orderLine.quantity
            if (index === -1) {
              totals.push({ ...price, amount })
            } else {
              totals[index].amount += amount
            }
          })
        }
      })
      const sorted = totals.sort((a, b) => b.quantity - a.quantity)
      return sorted.find(total => total.currency.type === currencyTypes.LEGAL) || sorted[0]
    },
    amountInCart () {
      var amount = 0
      this.orderLines.forEach((orderLine) => {
        if (!orderLine.parentOrderLine && orderLine.orderLineType === SALE && orderLine.itemEntity === PRODUCT) {
          amount += orderLine.quantity
        }
      })
      return amount
    },
    ...mapState(['activityReservations', 'activities']),
    ...mapGetters({
      getTotal: 'cart/getTotal',
      guestsInBookings: 'me/guests',
      cartProductOrderLines: 'cart/cartProductOrderLines',
      reservationMethod: 'activityReservations/reservationMethod',
      attendeeTypes: 'activityReservations/attendeeTypes',
      accountCurrencies: 'activityReservations/accountCurrencies',
      guestAccount: 'accounts/getGuestAccount',
    }),
  },
  methods: {
    findPricingGroup ({ pricingGroups }) {
      let applicablePGP = pricingGroups.find((pGP) => {
        return pGP.pricingGroupId === null
      })
      const account = this.activityReservations.edited.account
      if (account) {
        const accountApplicablePGP = pricingGroups.find((pGP) => {
          return pGP.pricingGroupId === account.pricingGroupId
        })
        if (accountApplicablePGP) {
          applicablePGP = accountApplicablePGP
        }
      }
      return applicablePGP
    },
  },
}
